import React, { useState, useEffect, useCallback } from "react";
import { useMutation, gql } from "@apollo/client";
import { styled } from "@mui/material/styles";
import {
  Grid,
  Typography,
  Box,
  FormControl,
  TextField,
  Switch,
  Button,
  Paper,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Select,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import ReactQuill from "react-quill";
import {
  Category as CategoryIcon,
  AutoAwesome as AutoAwesomeIcon,
  ContactPhone as ContactPhoneIcon,
  Assignment as AssignmentIcon,
  Chat as ChatIcon,
  Videocam as VideoIcon,
  Event as EventIcon,
  Send as SendIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

// Styled Components - Matching DisplayOptions.js/TextColors.js pattern
const ConfigPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  height: "100%",
  backgroundColor: theme.palette.background.paper,
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const SettingsToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: 8,
  marginBottom: theme.spacing(1),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
  },
}));

const QuillContainer = styled(Box)(({ theme }) => ({
  "& .ql-container": {
    borderRadius: "0 0 8px 8px",
    minHeight: 100,
    fontSize: 14,
  },
  "& .ql-toolbar": {
    borderRadius: "8px 8px 0 0",
    backgroundColor: theme.palette.grey[50],
  },
  "& .ql-editor": {
    minHeight: 80,
  },
}));

// Contact Option Cards
const ContactOptionCard = styled(Box)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : `2px solid ${theme.palette.divider}`,
  backgroundColor: selected
    ? theme.palette.primary.main + "08"
    : theme.palette.background.paper,
  cursor: "pointer",
  transition: "all 0.2s ease",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1),
  position: "relative",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    transform: "translateY(-2px)",
  },
}));

const ContactOptionIcon = styled(Box)(({ bgColor }) => ({
  width: 48,
  height: 48,
  borderRadius: "50%",
  backgroundColor: bgColor || "#1976d2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
}));

// Preview Components
const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: "#f5f7fa",
  minHeight: 500,
  display: "flex",
  flexDirection: "column",
}));

const ChatWindowPreview = styled(Box)({
  maxWidth: 350,
  backgroundColor: "#ffffff",
  borderRadius: 16,
  boxShadow: "0 5px 40px rgba(0,0,0,0.15)",
  overflow: "hidden",
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  height: 480,
});

const ChatHeaderPreview = styled(Box)(({ buttonColor, fontColor }) => ({
  backgroundColor: buttonColor || "#4CAF50",
  color: fontColor || "#ffffff",
  padding: "16px",
  fontWeight: 600,
  fontSize: "1rem",
  display: "flex",
  alignItems: "center",
  gap: 12,
}));

const ChatBodyPreview = styled(Box)({
  flex: 1,
  padding: 16,
  backgroundColor: "#f9fafb",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const ChatBubblePreview = styled(Box)(({ buttonColor }) => ({
  backgroundColor: buttonColor || "#4CAF50",
  color: "white",
  padding: "10px 14px",
  borderRadius: 16,
  borderBottomRightRadius: 4,
  maxWidth: "85%",
  alignSelf: "flex-end",
  fontSize: 14,
}));

const ContactButtonPreview = styled(Box)(({ buttonColor }) => ({
  backgroundColor: buttonColor || "#4CAF50",
  color: "white",
  padding: "8px 16px",
  borderRadius: 20,
  fontSize: 13,
  fontWeight: 500,
  textAlign: "center",
  cursor: "pointer",
  marginBottom: 4,
  alignSelf: "flex-end",
  display: "flex",
  alignItems: "center",
  gap: 6,
  "&:hover": {
    opacity: 0.9,
  },
}));

const ChatInputPreview = styled(Box)(({ theme }) => ({
  padding: 12,
  borderTop: "1px solid #e0e0e0",
  backgroundColor: "#fafafa",
}));

// Styled Dialog
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    padding: theme.spacing(1),
  },
}));

const UPDATE_WIDGET = gql`
  mutation UpdateWidget($id: ID!, $input: WidgetInput!) {
    updateWidget(id: $id, input: $input) {
      id
    }
  }
`;

// Contact options configuration
const contactOptionsConfig = [
  {
    key: "chat",
    label: "Chat",
    description: "Text messaging",
    icon: ChatIcon,
    color: "#4CAF50",
  },
  {
    key: "video",
    label: "Audio / Video",
    description: "Voice & video call",
    icon: VideoIcon,
    color: "#FF5722",
  },
  {
    key: "booking",
    label: "Book Meeting",
    description: "Schedule a meeting",
    icon: EventIcon,
    color: "#2196F3",
  },
];

function RequestOptions({
  handleChildData,
  widgetData,
  isEditing,
  widgetId,
  onSave,
}) {
  const [updateWidget] = useMutation(UPDATE_WIDGET);

  // State declarations
  const [isPreselection, setIsPreselection] = useState(false);
  const [automaticResponse, setAutomaticResponse] = useState(true);
  const [automaticResponseText, setAutomaticResponseText] = useState(
    "We are taking care of your request - pls stay tuned"
  );
  const [contactOptions, setContactOptions] = useState({
    chat: true,
    video: true,
    booking: true,
  });
  const [isLeadForm, setIsLeadForm] = useState(false);
  const [isPrivacyInformation, setIsPrivacyInformation] = useState(false);
  const [privacyText, setPrivacyText] =
    useState(`<p><strong>Privacy Policy</strong></p>
<p>We respect your privacy and are committed to protecting your personal information.</p>
<p><strong>Information We Collect:</strong></p>
<ul>
<li>Name and contact information you provide</li>
<li>Messages and communications through this chat</li>
<li>Technical data such as browser type and IP address</li>
</ul>
<p><strong>How We Use Your Information:</strong></p>
<ul>
<li>To respond to your inquiries and provide support</li>
<li>To improve our services and user experience</li>
<li>To communicate important updates</li>
</ul>
<p><strong>Data Protection:</strong></p>
<p>We implement appropriate security measures to protect your data. We do not sell or share your personal information with third parties without your consent.</p>
<p>For questions about our privacy practices, please contact us.</p>`);
  const [termsOfServiceText, setTermsOfServiceText] =
    useState(`<p><strong>Terms of Service</strong></p>
<p>By using this chat service, you agree to the following terms:</p>
<p><strong>Acceptable Use:</strong></p>
<ul>
<li>Use the chat for legitimate inquiries only</li>
<li>Do not submit false or misleading information</li>
<li>Do not use abusive or inappropriate language</li>
</ul>
<p><strong>Service Availability:</strong></p>
<p>We strive to provide reliable service but cannot guarantee uninterrupted availability. Response times may vary based on agent availability.</p>
<p><strong>Liability:</strong></p>
<p>Information provided through this chat is for general purposes only. We are not liable for decisions made based on chat communications.</p>
<p><strong>Changes to Terms:</strong></p>
<p>We reserve the right to modify these terms at any time. Continued use of the service constitutes acceptance of updated terms.</p>`);
  const [open, setOpen] = useState(false);

  // Get widget colors for preview
  const buttonColor = widgetData?.welcome_screen?.button_color || "#4CAF50";
  const fontColor = widgetData?.welcome_screen?.font_color || "#ffffff";
  const businessName =
    widgetData?.business_details?.display_name || "Support Chat";

  // Initialize state from widgetData when editing
  useEffect(() => {
    if (widgetData?.contact_options) {
      setContactOptions({
        chat: widgetData.contact_options.chat_enabled ?? true,
        video: widgetData.contact_options.video_enabled ?? true,
        booking: widgetData.contact_options.booking_enabled ?? true,
      });
    }
    if (widgetData?.request_routing) {
      setIsPreselection(
        widgetData.request_routing.preselection_enabled ?? false
      );
      setAutomaticResponse(
        widgetData.request_routing.automatic_response_enabled ?? true
      );
      setAutomaticResponseText(
        widgetData.request_routing.automatic_response_text ||
          "We are taking care of your request - pls stay tuned"
      );
      setIsLeadForm(widgetData.request_routing.lead_form_enabled ?? false);
      setIsPrivacyInformation(
        widgetData.request_routing.privacy_enabled ?? false
      );
      setPrivacyText(widgetData.request_routing.privacy_text || "");
      setTermsOfServiceText(
        widgetData.request_routing.terms_of_service_text || ""
      );
    }
  }, [widgetData]);

  const handleSaveRequestOptions = useCallback(async () => {
    if (!widgetId) {
      console.error("No widgetId available");
      return;
    }

    try {
      const widgetInput = {
        business_details: widgetData?.business_details
          ? {
              business_type: widgetData.business_details.business_type,
              name: widgetData.business_details.name,
              display_name: widgetData.business_details.display_name,
              website_url: widgetData.business_details.website_url,
            }
          : undefined,
        website_integration: widgetData?.website_integration
          ? {
              mode: widgetData.website_integration.mode,
              button_style: widgetData.website_integration.button_style,
              position: widgetData.website_integration.position,
              square_buttom: widgetData.website_integration.square_buttom,
              minimize_small_button:
                widgetData.website_integration.minimize_small_button,
              preview: widgetData.website_integration.preview,
              media: widgetData.website_integration.media,
              picture: widgetData.website_integration.picture,
              speeach_bubble_test:
                widgetData.website_integration.speeach_bubble_test,
              call_to_action_button_text:
                widgetData.website_integration.call_to_action_button_text,
              slogan: widgetData.website_integration.slogan,
            }
          : undefined,
        welcome_screen: widgetData?.welcome_screen
          ? {
              font_shadow: widgetData.welcome_screen.font_shadow,
              desc: widgetData.welcome_screen.desc,
              customizable_button:
                widgetData.welcome_screen.customizable_button,
              button_color: widgetData.welcome_screen.button_color,
              font_color: widgetData.welcome_screen.font_color,
              icon_color: widgetData.welcome_screen.icon_color,
              welcome_message: widgetData.welcome_screen.welcome_message,
            }
          : undefined,
        // Save contact options (Step 3)
        contact_options: {
          chat_enabled: contactOptions.chat,
          video_enabled: contactOptions.video,
        },
        // Save request routing settings (Step 3)
        request_routing: {
          preselection_enabled: isPreselection,
          automatic_response_enabled: automaticResponse,
          automatic_response_text: automaticResponseText,
          lead_form_enabled: isLeadForm,
          privacy_enabled: isPrivacyInformation,
          privacy_text: privacyText,
          terms_of_service_text: termsOfServiceText,
        },
        // Preserve existing data for other steps
        session_end: widgetData?.session_end
          ? {
              title: widgetData.session_end.title,
              closing_message: widgetData.session_end.closing_message,
              background_color: widgetData.session_end.background_color,
              text_color: widgetData.session_end.text_color,
              show_feedback_form: widgetData.session_end.show_feedback_form,
              rating_type: widgetData.session_end.rating_type,
            }
          : undefined,
        contact_form: widgetData?.contact_form
          ? {
              unavailable_message: widgetData.contact_form.unavailable_message,
              background_color: widgetData.contact_form.background_color,
              text_color: widgetData.contact_form.text_color,
              form_fields: widgetData.contact_form.form_fields?.map(
                ({ __typename, ...field }) => field
              ),
              privacy_enabled: widgetData.contact_form.privacy_enabled,
              privacy_text: widgetData.contact_form.privacy_text,
            }
          : undefined,
        visibility_settings: widgetData?.visibility_settings
          ? {
              always_on: widgetData.visibility_settings.always_on,
              custom_schedule_enabled:
                widgetData.visibility_settings.custom_schedule_enabled,
              timezone: widgetData.visibility_settings.timezone,
              schedule: widgetData.visibility_settings.schedule,
              hide_on_mobile: widgetData.visibility_settings.hide_on_mobile,
              hide_when_offline:
                widgetData.visibility_settings.hide_when_offline,
              dialog_delay_seconds:
                widgetData.visibility_settings.dialog_delay_seconds,
            }
          : undefined,
      };

      const { data } = await updateWidget({
        variables: { id: widgetId, input: widgetInput },
      });

      console.log("Request options saved successfully");
      return { widgetId, widgetData: data.updateWidget };
    } catch (err) {
      console.error("Error saving request options:", err);
      throw err;
    }
  }, [
    widgetId,
    widgetData,
    contactOptions,
    isPreselection,
    automaticResponse,
    automaticResponseText,
    isLeadForm,
    isPrivacyInformation,
    privacyText,
    termsOfServiceText,
    updateWidget,
  ]);

  // Expose save function to parent component
  useEffect(() => {
    if (onSave && typeof onSave === "function") {
      onSave(handleSaveRequestOptions);
    }
  }, [onSave, handleSaveRequestOptions]);

  const handleContactOptionToggle = (option) => {
    setContactOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  // Get enabled contact options for preview
  const enabledContactOptions = contactOptionsConfig.filter(
    (opt) => contactOptions[opt.key]
  );

  return (
    <Grid container spacing={4}>
      {/* Header Section */}
      <Grid item xs={12}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Customize Contact Options
        </Typography>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Configure how visitors can contact you. You can enable preselection
          screens to route inquiries to assigned team members (e.g., General /
          Product Information / Support).
        </Alert>
      </Grid>

      {/* Configuration Panel */}
      <Grid item xs={12} md={5}>
        <ConfigPanel elevation={3}>
          {/* Section 1: Preselection */}
          <Section>
            <SectionTitle variant="h6">
              <CategoryIcon color="primary" />
              Preselection Screen
            </SectionTitle>

            <SettingsToggle>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Enable Preselection
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Route inquiries to specific team members
                </Typography>
              </Box>
              <Switch
                checked={isPreselection}
                onChange={(e) => setIsPreselection(e.target.checked)}
                color="primary"
              />
            </SettingsToggle>

            {isPreselection && (
              <Box
                sx={{
                  mt: 2,
                  pl: 2,
                  borderLeft: "3px solid",
                  borderColor: "primary.main",
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpen(true)}
                  size="small"
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  Add New Routing
                </Button>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Routing Screen Buttons
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label="General"
                    onDelete={() => {}}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label="Product Info"
                    onDelete={() => {}}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label="Support"
                    onDelete={() => {}}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Stack>
              </Box>
            )}
          </Section>

          {/* Section 2: Automatic Response */}
          <Section>
            <SectionTitle variant="h6">
              <AutoAwesomeIcon color="primary" />
              Automatic Response
            </SectionTitle>

            <SettingsToggle>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Enable Automatic Response
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Send a message after visitor submits request
                </Typography>
              </Box>
              <Switch
                checked={automaticResponse}
                onChange={(e) => setAutomaticResponse(e.target.checked)}
                color="primary"
              />
            </SettingsToggle>

            {automaticResponse && (
              <Box sx={{ mt: 2 }}>
                <StyledTextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Response Message"
                  value={automaticResponseText}
                  onChange={(e) => setAutomaticResponseText(e.target.value)}
                  variant="outlined"
                  size="small"
                  helperText="This message is sent automatically after a visitor submits their request"
                />
              </Box>
            )}
          </Section>

          {/* Section 3: Contact Options */}
          <Section>
            <SectionTitle variant="h6">
              <ContactPhoneIcon color="primary" />
              Contact Options
            </SectionTitle>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block" }}
            >
              Select which communication channels are available to visitors
            </Typography>

            <Grid container spacing={2}>
              {contactOptionsConfig.map((option) => {
                const IconComponent = option.icon;
                const isSelected = contactOptions[option.key];
                return (
                  <Grid item xs={6} key={option.key}>
                    <ContactOptionCard
                      selected={isSelected}
                      onClick={() => handleContactOptionToggle(option.key)}
                    >
                      <ContactOptionIcon
                        bgColor={isSelected ? option.color : "#bdbdbd"}
                      >
                        <IconComponent fontSize="small" />
                      </ContactOptionIcon>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {option.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: "center" }}
                      >
                        {option.description}
                      </Typography>
                      {isSelected && (
                        <CheckIcon
                          fontSize="small"
                          color="primary"
                          sx={{ position: "absolute", top: 8, right: 8 }}
                        />
                      )}
                    </ContactOptionCard>
                  </Grid>
                );
              })}
            </Grid>
          </Section>

          {/* Section 4: Lead Form */}
          <Section>
            <SectionTitle variant="h6">
              <AssignmentIcon color="primary" />
              Lead Form
            </SectionTitle>

            <SettingsToggle>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Activate Lead Form
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Collect visitor info before starting conversation
                </Typography>
              </Box>
              <Switch
                checked={isLeadForm}
                onChange={(e) => setIsLeadForm(e.target.checked)}
                color="primary"
              />
            </SettingsToggle>

            {isLeadForm && (
              <Box
                sx={{
                  mt: 2,
                  pl: 2,
                  borderLeft: "3px solid",
                  borderColor: "primary.main",
                }}
              >
                <SettingsToggle sx={{ backgroundColor: "#fff", mb: 2 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Enable Privacy Information
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Show privacy policy checkbox
                    </Typography>
                  </Box>
                  <Switch
                    checked={isPrivacyInformation}
                    onChange={(e) => setIsPrivacyInformation(e.target.checked)}
                    color="primary"
                  />
                </SettingsToggle>

                {isPrivacyInformation && (
                  <>
                    <QuillContainer sx={{ mb: 3 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1, display: "block" }}
                      >
                        Privacy Policy Content
                      </Typography>
                      <ReactQuill
                        theme="snow"
                        value={privacyText}
                        onChange={setPrivacyText}
                        placeholder="Enter your privacy policy text here..."
                      />
                    </QuillContainer>
                    <QuillContainer>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1, display: "block" }}
                      >
                        Terms of Service Content
                      </Typography>
                      <ReactQuill
                        theme="snow"
                        value={termsOfServiceText}
                        onChange={setTermsOfServiceText}
                        placeholder="Enter your terms of service text here..."
                      />
                    </QuillContainer>
                  </>
                )}
              </Box>
            )}
          </Section>
        </ConfigPanel>
      </Grid>

      {/* Preview Panel */}
      <Grid item xs={12} md={7}>
        <PreviewContainer elevation={3}>
          <SectionTitle variant="h6" sx={{ mb: 2 }}>
            <ChatIcon color="primary" />
            Live Preview
          </SectionTitle>

          <Box
            display="flex"
            justifyContent="center"
            alignItems="flex-start"
            flex={1}
          >
            <ChatWindowPreview>
              {/* Chat Header */}
              <ChatHeaderPreview
                buttonColor={buttonColor}
                fontColor={fontColor}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ChatIcon sx={{ color: fontColor, fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ color: fontColor }}
                  >
                    {businessName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: fontColor, opacity: 0.8 }}
                  >
                    We're here to help
                  </Typography>
                </Box>
                <CloseIcon
                  sx={{ color: fontColor, opacity: 0.7, fontSize: 20 }}
                />
              </ChatHeaderPreview>

              {/* Chat Body */}
              <ChatBodyPreview>
                {/* Welcome Messages */}
                <ChatBubblePreview buttonColor={buttonColor}>
                  <Typography variant="body2">
                    You have questions about our products?
                  </Typography>
                </ChatBubblePreview>
                <ChatBubblePreview buttonColor={buttonColor}>
                  <Typography variant="body2">
                    We are happy to help you!
                  </Typography>
                </ChatBubblePreview>
                <ChatBubblePreview buttonColor={buttonColor}>
                  <Typography variant="body2">
                    How would you like to communicate with us?
                  </Typography>
                </ChatBubblePreview>

                {/* Contact Option Buttons */}
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 0.5,
                  }}
                >
                  {enabledContactOptions.map((option) => {
                    const IconComponent = option.icon;
                    return (
                      <ContactButtonPreview
                        key={option.key}
                        buttonColor={buttonColor}
                      >
                        <IconComponent sx={{ fontSize: 16 }} />
                        {option.label}
                      </ContactButtonPreview>
                    );
                  })}
                </Box>

                {/* Automatic Response Preview */}
                {automaticResponse && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: "#e3f2fd",
                      borderRadius: 2,
                      borderLeft: "3px solid #2196f3",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="primary"
                      fontWeight={600}
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Auto-Response
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {automaticResponseText}
                    </Typography>
                  </Box>
                )}
              </ChatBodyPreview>

              {/* Chat Input */}
              <ChatInputPreview>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Type your message..."
                  size="small"
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" size="small" disabled>
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 30,
                      backgroundColor: "white",
                    },
                  }}
                />
              </ChatInputPreview>
            </ChatWindowPreview>
          </Box>

          {/* Preview Info */}
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Preview uses colors from Text & Colors settings (Step 3)
            </Typography>
          </Box>
        </PreviewContainer>
      </Grid>

      {/* Add Routing Dialog */}
      <StyledDialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CategoryIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Add New Routing
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a new routing option that visitors can select. Inquiries will
            be forwarded to the assigned team.
          </Typography>
          <StyledTextField
            autoFocus
            label="Display Name"
            type="text"
            fullWidth
            size="small"
            sx={{ mb: 3 }}
            helperText="Name shown to visitors (e.g., Sales, Technical Support)"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Select User Group</InputLabel>
            <Select label="Select User Group" sx={{ borderRadius: 2 }}>
              {/* Add menu items here */}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ borderRadius: 2 }}
          >
            Add Routing
          </Button>
        </DialogActions>
      </StyledDialog>
    </Grid>
  );
}

export default RequestOptions;
